import { useAccountDelete, useIdentityGet } from "@sportsmet/auth-sdk/private";
import { useProfileGet } from "@sportsmet/profile-sdk/private";
import {
  Button,
  confirmDestructive,
  resolveAccountIdentifier,
} from "@sportsmet/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useRef, useState } from "react";

import { useAuth } from "../../auth/AuthProvider";
import { clearSessionTokens } from "../../auth/sessionCookies";
import { showApiError } from "../../errors/apiErrorBus";
import {
  buildDeleteRequestId,
  DELETE_ACCOUNT_BODY,
  DELETE_ACCOUNT_CONFIRM_LABEL,
  DELETE_ACCOUNT_LAST_SUPER_ADMIN_MESSAGE,
  DELETE_ACCOUNT_TITLE,
  LAST_SUPER_ADMIN_CODE,
} from "./deleteAccountCopy";

export const DeleteAccountControl = (): ReactNode => {
  const identity = useIdentityGet({});
  const profile = useProfileGet({});
  const deleteAccount = useAccountDelete();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { refreshSession } = useAuth();
  const isRunning = useRef(false);
  const [lastSuperAdminMessage, setLastSuperAdminMessage] = useState<
    string | null
  >(null);

  const userId = identity.data?.data?.userId ?? null;
  const identifier =
    userId === null
      ? null
      : resolveAccountIdentifier({
          username: profile.data?.data?.profile?.username ?? null,
          email: identity.data?.data?.email ?? null,
          userId,
        });

  async function handleDelete(): Promise<void> {
    if (isRunning.current) return;
    isRunning.current = true;
    if (identifier === null) {
      isRunning.current = false;
      return;
    }

    const isConfirmed = await confirmDestructive({
      title: DELETE_ACCOUNT_TITLE,
      body: DELETE_ACCOUNT_BODY,
      confirmLabel: DELETE_ACCOUNT_CONFIRM_LABEL,
      typeToConfirm: { label: identifier.label, value: identifier.value },
    });
    if (!isConfirmed) {
      isRunning.current = false;
      return;
    }

    setLastSuperAdminMessage(null);

    let result;
    try {
      result = await deleteAccount.mutateAsync({
        requestId: buildDeleteRequestId(),
      });
    } catch {
      showApiError({
        error: { code: "INTERNAL_CALL_FAILED", message: "" },
      });
      isRunning.current = false;
      return;
    }

    if (result.error?.code === LAST_SUPER_ADMIN_CODE) {
      setLastSuperAdminMessage(DELETE_ACCOUNT_LAST_SUPER_ADMIN_MESSAGE);
      isRunning.current = false;
      return;
    }

    if (showApiError(result)) {
      isRunning.current = false;
      return;
    }

    clearSessionTokens();
    queryClient.clear();
    // signOut() would call session/revoke against an account that no longer
    // exists. Refreshing with no token is AuthProvider's local-only path: it
    // re-reads the cookies so the shell drops Sign out and the nav.
    await refreshSession();
    void navigate({
      to: "/sign-in",
      search: { step: "email" as const, deleted: true },
      replace: true,
    });
  }

  return (
    <>
      <Button
        variant="destructive"
        fullWidth
        className="md:w-auto md:self-start"
        disabled={identifier === null}
        loading={deleteAccount.isPending}
        onClick={() => {
          void handleDelete();
        }}
        data-testid="delete-account-button"
      >
        Delete account
      </Button>
      {lastSuperAdminMessage === null ? null : (
        <p
          role="alert"
          className="text-body text-error"
          data-testid="delete-account-refusal"
        >
          {lastSuperAdminMessage}
        </p>
      )}
    </>
  );
};
